import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  inject
} from '@angular/core';
import { DUAL_CURRENCY_DEPOSIT } from '@approot/constants';
import {
  DvEntities,
  Leg,
  ProductGroupings,
  ProductSpecification,
  Strategy,
  TeamMember
} from '@approot/models';
import { LegsToAggregate } from '@approot/models/summed-legs';
import {
  AdminService,
  AlertsService,
  AuthService,
  ComponentVisibilityService,
  ConfigService,
  LegService,
  MARKET_DATA_AUTO_REFRESH,
  MARKET_DATA_LIVE,
  MARKET_DATA_PAST_DATE,
  ProductsConfigService,
  RfqService,
  StrategiesService,
  StrategyDetailService,
  Tab,
  TradesCaptureService,
  marketDataDateType
} from '@approot/services';
import {
  isAnExpDateInThePast,
  rfqTabTitleMapper
} from '@approot/services/rfq/rfq-creation';
import {
  breakDownLongName,
  legComponentsToRfqBuilderLegs,
  shouldBreakdownStrategy
} from '@approot/services/rfq/rfq-tab/rfq-tab';
import { DateUtilities, SubscriptionSink, clone } from '@approot/utilities';
import copy from 'copy-to-clipboard';
import { Throttle } from 'lodash-decorators';
import { firstValueFrom, fromEvent, map } from 'rxjs';
import { LegComponent } from './leg/leg.component';
import { LegMenuProductsPipe } from './pipes/leg-menu-products/leg-menu-products.pipe';
import { SumLegComponent } from './sum-leg/sum-leg.component';
type ShortName = { shortName: string };

export function sortByShortName(arr: string[]) {
  return (a: ShortName, b: ShortName) =>
    arr.indexOf(a.shortName) - arr.indexOf(b.shortName);
}

@Component({
  selector: 'app-strategy-detail',
  templateUrl: './strategy-detail.component.html',
  styleUrls: ['./strategy-detail.component.scss']
})
export class StrategyDetailComponent
  implements AfterContentChecked, OnInit, OnDestroy, AfterViewInit
{
  public breakingDownStrategy = false;
  @ViewChildren(LegComponent)
  public legComponentsQueryList: QueryList<LegComponent>;
  @ViewChild(SumLegComponent) private sumLegComponent: SumLegComponent;
  rfqOpen = false;
  spotLadderOpen = false;
  ratesLadderOpen = false;
  spotSpotMatrixOpen = false;
  saveStrategyOpen = false;
  deltaByTenorOpen = false;
  vegaByBucketOpen = false;
  payoutGraphOpen = false;
  shareStrategyOpen = false;
  public isAllCheckedLegsValid = false;
  public multiLegSolver = false;
  public rfqButtonSettings: {
    rfqDisabled: boolean;
    rfqErrorMsg: string;
  } = {
    rfqDisabled: true,
    rfqErrorMsg: ''
  };
  lastCurrentDateUsed: marketDataDateType = MARKET_DATA_LIVE;
  activeCurrencyPairs: string[] = []; // currency pairs that are selected in legs
  selectedLegs: LegComponent[];
  productGroupings: ProductGroupings[];
  autoRefreshIntervals: any; // the refreshing interval is stored here
  public analyticalTools = [
    {
      name: 'Delta by Tenor',
      enabledFn: () => this.isAnalyticalToolsAccessAuthorized
    },
    {
      name: 'Vega by Bucket',
      enabledFn: () => this.isAnalyticalToolsAccessAuthorized
    },
    {
      name: 'Spot Ladder',
      enabledFn: () => this.isAnalyticalToolsAccessAuthorized
    },
    {
      name: 'Rates Ladder',
      enabledFn: () => this.isAnalyticalToolsAccessAuthorized
    },
    {
      name: 'Spot Spot Matrix',
      enabledFn: () => this.isAnalyticalToolsAccessAuthorized
    },
    {
      name: 'Multi Leg Solver',
      enabledFn: () => this.isAnalyticalToolsAccessAuthorized
    },
    {
      name: 'Payout Graph',
      enabledFn: () =>
        this.isAnalyticalToolsAccessAuthorized && this.canPriceAll()
    }
  ];

  sumOfLegsItems: { legs: LegComponent[]; useReportingCurrency: boolean };
  //order in which to display the market data results
  private breakdownRfqLegs = false;
  private subSink = new SubscriptionSink();
  private strategyDetailService = inject(StrategyDetailService);
  private isAnalyticalToolsAccessAuthorized: boolean;
  public tabsState = this.strategyDetailService.tabsState;
  public parent: DvEntities;
  constructor(
    private legService: LegService,
    private configService: ConfigService,
    private strategiesService: StrategiesService,
    private rfqService: RfqService,
    private renderer: Renderer2,
    private cdref: ChangeDetectorRef,
    private adminService: AdminService,
    private tradesCaptureService: TradesCaptureService,
    private legMenuProductsPipe: LegMenuProductsPipe,
    private authService: AuthService,
    private productsConfigService: ProductsConfigService,
    private componentVisibilityService: ComponentVisibilityService,
    private alerts: AlertsService
  ) {}

  public ngAfterViewInit(): void {
    this.parent = this.configService.userSettings().parent;
    if (this.parent.enableTradeRecaps) {
      this.analyticalTools = [
        ...this.analyticalTools,
        {
          name: 'Email Trade Recap',
          enabledFn: this.tradeRecapEnabled.bind(this)
        }
      ];
    }
    this.subSink.sink = this.legComponentsQueryList?.changes.subscribe(
      legComponents => {
        this.handleRfqButtonState();
        this.strategyDetailService.currentLegComponents =
          legComponents.toArray();

        setTimeout(_ => {
          const tabState = this.tabsState().tabs[this.tabsState().selectedTab];
          if (tabState?.tradeViewMode) {
            this.changeInLegCounterParty();
          }
        }, 0);
      }
    );

    this.subSink.sink = this.rfqService.loginState$.subscribe(_ => {
      this.handleRfqButtonState();
    });
  }

  public hideStructuringPanel() {
    this.componentVisibilityService.showStructuringPanel = false;
  }

  get autoRefreshCode(): marketDataDateType {
    return MARKET_DATA_AUTO_REFRESH;
  }

  get liveDataCode(): marketDataDateType {
    return MARKET_DATA_LIVE;
  }

  get pastDateCode(): marketDataDateType {
    return MARKET_DATA_PAST_DATE;
  }

  get legComponents() {
    return this.legComponentsQueryList?.toArray();
  }

  public restoreLoadedTabs(): void {
    this.strategyDetailService.restoreLoadedTabs();
    const tabsState = this.tabsState();
    this.changeInSelectedTab(tabsState.tabs[tabsState.selectedTab]);
  }

  public doNotRestoreTabs(): void {
    this.strategyDetailService.setTabsAsReady();
  }

  public handleProductConfigs(): void {
    this.breakdownRfqLegs =
      this.configService.userSettings().breakDownVanillaStrategiesInRfqPanel;
    this.productGroupings = this.productsConfigService.productGroupings;
    // if a group has no available products this shouldn't be shown
    if (
      this.productGroupings &&
      this.productsConfigService.productInfo.productSpecifications
    ) {
      this.productGroupings = this.productGroupings.filter(group =>
        group.items.find((productItem: string) =>
          this.productsConfigService.productInfo.productSpecifications.find(
            productSpec => productSpec.productName === productItem
          )
        )
      );
    }
  }

  public get showDirectRfq(): number {
    return this.productsConfigService.productInfo?.directRfqProducts?.length;
  }

  get showTradeView(): boolean {
    return this.tradesCaptureService.displayTradeViewButton;
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
    clearInterval(this.strategyDetailService.savingTabInterval);
  }

  ngOnInit(): void {
    this.isAnalyticalToolsAccessAuthorized =
      this.configService.userSettings().isAnalyticalToolsAccessAuthorized;
    this.handleProductConfigs();
    this.subSink.sink = fromEvent(document, 'keydown').subscribe(event => {
      // we want to listen to the key presses
      this.keypress(event);
    });
    this.subSink.sink = this.legService.acrossLegsValueChange
      .pipe(
        map(
          (change: {
            key: string;
            value: string;
            sourceLegIndex: number;
            textColor: string;
          }) => this.handleAcrossLegsValueChange(change)
        )
      )
      .subscribe();
  }

  handleAcrossLegsValueChange(change: {
    key: string;
    value: string;
    sourceLegIndex: number;
    textColor: string;
  }) {
    if (change && change.key && change.value) {
      this.legComponentsQueryList.forEach(legComponent => {
        if (
          legComponent.form.contains(change.key) &&
          legComponent.leg.checked
        ) {
          const dynamicLegAttribute = legComponent.legComponents.find(
            _ => _.attribute.key === change.key
          );
          if (dynamicLegAttribute) {
            const oldValue = legComponent.form.controls[change.key].value;
            if (change.value === oldValue) {
              return;
            }
            dynamicLegAttribute.nestedHtmlInputElement.focus();
            legComponent.form.controls[change.key].setValue(change.value);
            legComponent.form.controls[change.key].updateValueAndValidity();
            dynamicLegAttribute.nestedHtmlInputElement.blur();
          }

          const recipientDynamicLegAttributes =
            legComponent.legComponents.filter(
              _ => legComponent.legIndex !== change.sourceLegIndex
            );

          recipientDynamicLegAttributes.forEach(
            recipientDynamicLegAttribute => {
              if (
                recipientDynamicLegAttribute.attribute.key === 'strike' &&
                recipientDynamicLegAttribute.attribute.info
              ) {
                recipientDynamicLegAttribute.attribute.info = '';
              }
              if (change.key === recipientDynamicLegAttribute.attribute.key) {
                recipientDynamicLegAttribute.attribute.textColor =
                  change.textColor;
                if (
                  recipientDynamicLegAttribute.attribute.key === 'notional' &&
                  change.textColor === 'grey'
                ) {
                  this.updateLegAttributeColor(
                    legComponent,
                    'counterNotional',
                    'var(--pure-white)'
                  );
                } else if (
                  recipientDynamicLegAttribute.attribute.key === 'notional' &&
                  change.textColor === 'var(--pure-white)'
                ) {
                  this.updateLegAttributeColor(
                    legComponent,
                    'counterNotional',
                    'grey'
                  );
                } else if (
                  recipientDynamicLegAttribute.attribute.key ===
                    'counterNotional' &&
                  change.textColor === 'grey'
                ) {
                  this.updateLegAttributeColor(
                    legComponent,
                    'notional',
                    'var(--pure-white)'
                  );
                } else if (
                  recipientDynamicLegAttribute.attribute.key ===
                    'counterNotional' &&
                  change.textColor === 'var(--pure-white)'
                ) {
                  this.updateLegAttributeColor(
                    legComponent,
                    'notional',
                    'grey'
                  );
                }
              }
            }
          );
        }
      });
    }
  }

  changeInLegs(): void {
    if (!this.legComponentsQueryList) {
      return;
    }

    if (this.legs.length > this.legComponentsQueryList.length) {
      // detect new legs that are added
      this.cdref.detectChanges();
    }
    this.isAllCheckedLegsValid =
      this.checkedLegs.length > 0 &&
      !this.checkedLegs.some(leg => !leg.form.valid);

    this.handleRfqButtonState();
  }

  public onLegPricingChanges(legDeleted = false): void {
    setTimeout(_ => {
      if (legDeleted) {
        this.updateLegSumEntities();
      }
      const aggregationLegs = this.legsToBeSummed;
      const containsDcd = aggregationLegs?.legs.find(
        legComponent => legComponent.leg.productType === DUAL_CURRENCY_DEPOSIT
      );

      if (containsDcd) {
        this.updateLegsToAggregate(null);
        return;
      }

      const summingData = aggregationLegs?.legs.map(legComponent => {
        return {
          result: legComponent.leg.pricedLeg,
          isMidSelected: legComponent.leg.isMidToggled
        };
      });

      this.updateLegsToAggregate({
        legs: summingData,
        useReportingCurrency: aggregationLegs?.useReportingCurrency
      });
    }, 0);
  }

  public changeInLegCounterParty(): void {
    setTimeout(_ => {
      this.updateLegSumEntities();
    }, 0);
  }

  private updateLegSumEntities(): void {
    if (this.sumLegComponent) {
      this.sumLegComponent.setEntities(
        this.checkedLegs.map(legComponent => legComponent.form.value)
      );
    }
  }

  @Throttle(500)
  get rfqDisabled(): boolean {
    return this.rfqService.isRfqDisabled || this.rfqButtonSettings.rfqDisabled;
  }

  keypress(event) {
    if (event.keyCode === 120 && !this.anyModalsOpened) {
      // if the key is f9, price the valid legs
      this.legComponentsQueryList
        .filter(leg => leg.form.valid)
        .forEach(validLeg => {
          validLeg.legPricingService.price(true);
        });
    }
  }

  get anyModalsOpened() {
    // are any modals open
    return !!document.querySelector('.modal');
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  @Throttle(1000)
  get showRfqButton(): boolean {
    return this.rfqService.showRfq;
  }

  selectMarketDateLiveOrAuto() {
    // this occurs when we switch to using live/auto refreshing data
    const tabInfo = this.strategyDetailService.currentTab;
    const currentTabMarketData = tabInfo.marketData;
    if (currentTabMarketData.marketDataToUse === MARKET_DATA_PAST_DATE) {
      // if we moved from using a past date to a live/auto refresh
      if (
        currentTabMarketData.lastCurrentDateUsed === MARKET_DATA_AUTO_REFRESH
      ) {
        // if the button says auto refresh, then return to refreshing the data
        this.tabsState.update(state => {
          const clonedState = clone(state);
          const tab = clonedState.tabs[clonedState.selectedTab].marketData;
          tab.marketDataToUse = MARKET_DATA_AUTO_REFRESH;
          return clonedState;
        });
        this.autoRefreshLegs(); // resume the auto refresh interval
      } else {
        this.tabsState.update(state => {
          const clonedState = clone(state);
          const tab = clonedState.tabs[clonedState.selectedTab].marketData;
          tab.marketDataToUse = MARKET_DATA_LIVE;
          return clonedState;
        });
      }
    } else if (currentTabMarketData.marketDataToUse === MARKET_DATA_LIVE) {
      // if we clicked on live, now we want to do auto refresh
      // if the live button is clicked
      this.tabsState.update(state => {
        const clonedState = clone(state);
        const tab = clonedState.tabs[clonedState.selectedTab].marketData;
        tab.marketDataToUse = MARKET_DATA_AUTO_REFRESH;
        return clonedState;
      });
      this.autoRefreshLegs(); // start the auto refresh interval
    } else if (
      currentTabMarketData.marketDataToUse === MARKET_DATA_AUTO_REFRESH
    ) {
      this.tabsState.update(state => {
        const clonedState = clone(state);
        const tab = clonedState.tabs[clonedState.selectedTab].marketData;
        tab.marketDataToUse = MARKET_DATA_LIVE;
        return clonedState;
      });
      clearInterval(this.autoRefreshIntervals); // clear the interval
    }

    this.tabsState.update(state => {
      const clonedState = clone(state);
      const tab = clonedState.tabs[clonedState.selectedTab].marketData;
      tab.lastCurrentDateUsed = currentTabMarketData.marketDataToUse; // store whether we are using live data or auto refresh data.
      return clonedState;
    });
  }

  autoRefreshLegs() {
    // refresh all legs every 5 seconds
    clearInterval(this.autoRefreshIntervals); // clear the interval
    this.autoRefreshIntervals = setInterval(() => {
      this.checkedLegs
        .filter(leg => leg.form.valid) // get all the checked legs that are valid and can be priced
        .forEach(validLeg => {
          if (!validLeg.leg.pricingErrorMsg) {
            validLeg.legPricingService.price(true); // price the leg if no error message, and do not show spinner
          }
        });
    }, 5000);
  }

  getProductFromShortName(shortName: string): ProductSpecification {
    // gets a product from knowing its short name
    return this.legMenuProductsPipe.transform(shortName);
  }

  freezeDate() {
    // when we want to go into freeze date mode, we are no longer using live market data
    if (this.autoRefreshIntervals) {
      clearInterval(this.autoRefreshIntervals);
    }
    this.tabsState.update(state => {
      const clonedState = clone(state);
      const tab = clonedState.tabs[clonedState.selectedTab].marketData;
      tab.marketDataToUse = MARKET_DATA_PAST_DATE;
      return clonedState;
    });
  }

  get productInfo() {
    return this.productsConfigService.productInfo;
  }

  public createRfq(): void {
    const checkedLegs = this.checkedLegs;
    if (shouldBreakdownStrategy(checkedLegs, this.breakdownRfqLegs)) {
      this.breakDownRfq(clone(checkedLegs));
    } else {
      this.rfqService.addNewTab(
        legComponentsToRfqBuilderLegs(checkedLegs),
        null,
        null,
        false
      );
    }
  }

  private breakDownRfq(checkedLegs: LegComponent[]): void {
    const products = this.legService.createProductsObject(
      checkedLegs.map(legComponent => {
        return {
          product: legComponent.form.value,
          attributes: legComponent.leg.attributes
        };
      })
    );
    this.breakingDownStrategy = true;
    this.rfqService.breakDownVanillaStrategy(products).subscribe({
      next: resp => {
        this.breakingDownStrategy = false;
        this.rfqService.addNewTab(
          legComponentsToRfqBuilderLegs(checkedLegs, resp.response.products),
          breakDownLongName(checkedLegs),
          null,
          false
        );
      },
      error: _ => (this.breakingDownStrategy = false)
    });
  }

  public changeInSelectedTab(tab: Tab): void {
    this.freezeDateBlur(tab.marketData.frozenTime, true);
    this.legService.legs = [];

    setTimeout(() => {
      this.legService.legs = clone(tab.legs);
      this.legService.updateActiveAttributes('update', {}, tab.tradeViewMode);
    }, 0);

    if (tab.marketData.marketDataToUse === MARKET_DATA_AUTO_REFRESH) {
      this.autoRefreshLegs(); // resume the auto refresh interval
    } else {
      clearInterval(this.autoRefreshIntervals); // clear the interval
    }
  }

  freezeDateBlur(event: FocusEvent | string, loadedFromTab = false): void {
    // when the date text box is blurred
    const date = typeof event === 'string' ? event : event.target['value'];
    if (
      DateUtilities.isPrettyDate(date) ||
      DateUtilities.isDateOfDashedFormat(date) ||
      DateUtilities.isDateOfSlashFormat(date)
    ) {
      // if its a date of an understandable format
      let formatedDate; // stores the formated date of type yyyy-mm-dd
      if (DateUtilities.isPrettyDate(date)) {
        formatedDate = DateUtilities.prettyDateToDate(date);
      } else {
        // if the date isn't already in the pretty date format, we need to convert the date into this format in the date box
        if (DateUtilities.isDateOfSlashFormat(date)) {
          formatedDate = DateUtilities.restructureDate(date);
        } else {
          formatedDate = date;
        }
        this.setDateBox(formatedDate); // set the text box to be in a pretty date format
      }

      const dateObj = new Date(formatedDate);
      if (
        new Date().setHours(0, 0, 0, 0) > dateObj.setHours(0, 0, 0, 0) &&
        dateObj.getDay() !== 0 &&
        dateObj.getDay() !== 6
      ) {
        // the date must be in the past or today, and must not be a weekend
        this.tabsState.update(state => {
          const clonedState = clone(state);
          clonedState.tabs[clonedState.selectedTab].marketData.frozenTime =
            formatedDate;
          return clonedState;
        });
        this.freezeDate();
      } else {
        // if its not a valid date wipe the textbox and set the market data to live
        if (
          !event['relatedTarget'] ||
          event['relatedTarget'].id !== 'live-refresh-button'
        ) {
          // if they clicked on the live/refresh button, the click event will handle the switching
          // from a static date to the current date (auto refresh or live)
          if (!loadedFromTab) {
            this.selectMarketDateLiveOrAuto();
          }
        }
        this.tabsState.update(state => {
          const clonedState = clone(state);
          clonedState.tabs[clonedState.selectedTab].marketData.frozenTime = '';
          return clonedState;
        });
        this.setDateBox('');
      }
    } else {
      // if the date is not of valid format, then wipe the date set
      this.setDateBox('');
      this.tabsState.update(state => {
        const clonedState = clone(state);
        clonedState.tabs[clonedState.selectedTab].marketData.frozenTime = '';
        return clonedState;
      });
      if (
        !event['relatedTarget'] ||
        event['relatedTarget'].id !== 'live-refresh-button'
      ) {
        if (!loadedFromTab) {
          this.selectMarketDateLiveOrAuto();
        }
      }
    }
  }

  dateChange(event) {
    // this function is used for when the user clicks the calendar button
    // this occurs before the user clicks of the focused date

    // event is a date from clarity date picker
    if (event) {
      this.setDateBox(event);
    }
  }

  setDateBox(date) {
    const tempDate = date ? DateUtilities.dateToPrettyDate(date) : '';
    // if no date is set clear the date box
    setTimeout(() => {
      // clarity date picker functions override this
      this.renderer.setProperty(
        document.getElementById('market-data__date'),
        'value',
        tempDate
      );
    }, 5);
  }

  // openNewWindow() {
  //   window.open('http://localhost:4200/execution', '_blank');
  // }

  openModal(toolName) {
    switch (toolName) {
      case 'Spot Ladder':
        this.onSpotLadderOpenChanged(true);
        break;
      case 'Rates Ladder':
        this.onRatesLadderOpenChanged(true);
        break;
      case 'Spot Spot Matrix':
        this.onSpotSpotMatrixChanged(true);
        break;
      case 'share-strategy':
        this.onShareStrategyOpenChanged(true);
        break;
      case 'save-strategy':
        this.onSaveStrategyOpenChanged(true);
        break;
      case 'Delta by Tenor':
        this.onDeltaByTenorChanged(true);
        break;
      case 'Vega by Bucket':
        this.onVegaByBucketChanged(true);
        break;
      case 'Multi Leg Solver':
        this.showMultiLegSolver(true);
        break;
      case 'Payout Graph':
        this.onPayoutGraphChanged(true);
        break;
      case 'Email Trade Recap':
        this.emailTradeRecap();
        break;
      default:
        break;
    }
  }

  onShareStrategyOpenChanged(open: boolean) {
    this.shareStrategyOpen = open;
  }

  onSaveStrategyOpenChanged(open: boolean) {
    this.saveStrategyOpen = open;
  }

  onSpotLadderOpenChanged(open: boolean) {
    this.spotLadderOpen = open;
  }

  onRatesLadderOpenChanged(open: boolean) {
    this.ratesLadderOpen = open;
  }

  onDeltaByTenorChanged(open: boolean) {
    this.deltaByTenorOpen = open;
  }

  onVegaByBucketChanged(open: boolean) {
    this.vegaByBucketOpen = open;
  }

  onSpotSpotMatrixChanged(open: boolean) {
    this.spotSpotMatrixOpen = open;
  }

  showMultiLegSolver(open: boolean): void {
    this.multiLegSolver = open;
  }

  onPayoutGraphChanged(open: boolean): void {
    this.payoutGraphOpen = open;
  }

  public emailTradeRecap() {
    const trades = this.legService.createProductsObject(
      this.checkedLegs.map(legComponent => {
        return {
          product: legComponent.form.getRawValue(),
          attributes: legComponent.leg.attributes
        };
      })
    );
    this.rfqService
      .tradeService({ task: 'getSampleTradeRecap', trades }, true)
      .subscribe({
        next: resp => {
          if (resp.error) {
            this.alerts.addAlert({
              message: 'Unable to send trade recap email',
              type: 'error'
            });
            return;
          }
          const text = resp.description;
          if (text) {
            copy(text, { format: 'text/html' });
          }

          this.alerts.addAlert({
            message: 'Trade recap email sent (copied to clipboard)',
            type: 'success'
          });
        },
        error: () => {
          this.alerts.addAlert({
            message: 'Unable to send trade recap email',
            type: 'error'
          });
        }
      });
  }

  isEmpty(): boolean {
    return !this.legService.legs || this.legService.legs.length === 0;
  }

  get checkedLegs() {
    // get all legs that have been checked
    return (
      this.legComponentsQueryList?.filter(
        leg => leg.leg?.checked && leg.legHelperService.legAlive
      ) || []
    );
  }

  get legsToBeSummed(): {
    legs: LegComponent[];
    useReportingCurrency: boolean;
  } {
    const checkedLegsComponents = this.checkedLegs;
    // const checkedLegs

    if (checkedLegsComponents.length < 2) {
      return null;
    }

    const crosses = checkedLegsComponents.map(checkedLeg => {
      return checkedLeg.form.get('cross')
        ? checkedLeg.form.get('cross').value
        : '';
    }); // get all of the crosses of the legs

    return {
      legs: checkedLegsComponents,
      useReportingCurrency: !crosses.every(
        (val, _, arr) => val === arr[0] && !!val
      )
    };
  }

  private async rfqToolTipMessage() {
    let errorMessages = [];
    const loginState = await firstValueFrom(this.rfqService.loginState$);

    if (!loginState) {
      errorMessages.push('Please select a trading Entity');
    }

    if (!this.isMultiLegBuyFirst) {
      errorMessages.push(`The 1st leg must be 'Buy' for multi-leg RFQs`);
    }

    this.checkedLegs.forEach((leg, index) => {
      if (!leg.form.valid) {
        errorMessages.push(`There is an issue on checked leg ${index + 1}`);
      }
    });
    this.rfqButtonSettings.rfqErrorMsg = errorMessages.join('\n');
  }

  private get isMultiLegBuyFirst(): boolean {
    if (this.legComponentsQueryList?.length) {
      const checkedLegs = this.checkedLegs;
      if (checkedLegs.length < 2) {
        return true;
      }
      const formVal = checkedLegs[0].form.getRawValue();
      const position = formVal.position;

      if (position && position !== 'Buy') {
        return false;
      }
    }

    return true;
  }

  get isRfqDisabled(): boolean {
    // checks that there are legs checked, and they are all valid
    if (this.legComponentsQueryList) {
      const checkedLegs = this.checkedLegs; // get all legs that have been checked

      if (checkedLegs.length) {
        // if there are legs checked check that all these legs are valid
        const disabledLeg = checkedLegs.find(leg => leg.isLegDisabled); // get all legs that are valid
        if (!disabledLeg) {
          return !(
            this.configService.userSettings().usePerseus ||
            ((shouldBreakdownStrategy(checkedLegs, this.breakdownRfqLegs) ||
              rfqTabTitleMapper(
                legComponentsToRfqBuilderLegs(checkedLegs),
                this.configService.userSettings(),
                false
              )) &&
              this.isMultiLegBuyFirst &&
              !isAnExpDateInThePast(checkedLegs.map(leg => leg.form.value)))
          ); // is there a title that can be mapped to the formation of the legs
        }
      }
    }
    return true;
  }

  addProduct(product: ProductSpecification) {
    let preset;
    // if the user wants to apply their defaults when they add a leg:
    if (this.adminService.useDefaults) {
      // add the default values to the preset
      let currentDefaults = this.adminService.attributeDefaults;
      preset = {};
      currentDefaults.forEach(x => {
        let shortName =
          this.productsConfigService.productInfo.attributesDisplayList.filter(
            y => y.longName === x.key
          )[0].shortName;
        preset[shortName] = x.value;
      });
    }

    this.strategyDetailService.addLegToStructuringScreen(
      product.productType.longName.replace(/\s+/g, '').toLowerCase(),
      true,
      preset,
      this.strategyDetailService.tradingMode
    );
  }

  onStrategyShared(strategyDetails: {
    name: string;
    selectedMember: TeamMember;
    message: string;
  }) {
    // for each person who the strategy is being shared with
    // create a copy of the strategy, and set the userId to
    // person with whom the strategy is being shared, and set shared
    // equal to the userId of the person doing the sharing
    this.authService
      .currentSession()
      .then(data => {
        //strategyDetails.selectedMembers.forEach(teamMember => {
        let newStrategy: Strategy = {
          userId: strategyDetails.selectedMember.sub, // userId of sharee
          name: strategyDetails.name,
          shared: data.getIdToken().payload.sub, // userId of sharer
          timestamp: new Date().getTime(),
          products: this.legComponentsQueryList.map((l: LegComponent) => {
            let product = this.productsConfigService.productFormatter(
              l.form.value,
              l.leg.attributes
            );

            if (
              l.leg.productType === 'newdcd' &&
              product['margin'] !== undefined
            ) {
              // if the product is a newdcd
              product['margin'] = '';
            }
            return product;
          })
        };
        // only add message if it's not empty, can't insert
        // empty value into dynamodb
        if (strategyDetails.message) {
          newStrategy.message = strategyDetails.message;
        }
        this.strategiesService.saveStrategy(newStrategy, true);
      })
      .catch(err => console.error('onStrategyShared', err));
  }

  get activeExtraAttributesLength() {
    return this.legService.activeExtraAttributes.length || 0;
  }

  get activeDcdAttributesLength() {
    return this.legService.activeDcdAttributes.length || 0;
  }

  get legs() {
    return this.legService.legs || [];
  }

  get productSpecifications() {
    return this.productsConfigService.productInfo.productSpecifications || [];
  }

  setTrades(currentMode: boolean) {
    if (!currentMode) {
      this.legComponents.forEach(leg => (leg.leg.pricedLeg = null));
    }
    this.strategyDetailService.setTabTradingMode(!currentMode);
    this.legService.updateActiveAttributes(
      'update',
      {},
      this.strategyDetailService.tradingMode
    );
  }

  public drop(ev: CdkDragDrop<Leg[]>): void {
    moveItemInArray(ev.container.data, ev.previousIndex, ev.currentIndex);
  }

  @Throttle(500)
  public canPriceAll() {
    if (
      !this.legComponentsQueryList ||
      this.legComponentsQueryList.length === 0
    ) {
      return false;
    }
    return this.legComponentsQueryList.some(leg => !leg.isLegDisabled);
  }

  @Throttle(500)
  public tradeRecapEnabled() {
    if (!this.parent.enableTradeRecaps) {
      return false;
    }
    const tabsInfo = this.tabsState();
    const tabInfo = tabsInfo.tabs[tabsInfo.selectedTab];
    return this.canPriceAll() && tabInfo.tradeViewMode;
  }

  public doPriceAll() {
    this.checkedLegs.forEach(leg => leg.onPricingSubmit());
  }

  public requestInHouseTermSheet(): void {
    const products = this.legService.createProductsObject(
      this.checkedLegs.map(legComponent => ({
        product: legComponent.form.value,
        attributes: legComponent.leg.attributes
      }))
    );

    const payload = {
      task: 'requestInHouseTermSheet',
      trades: products
    };

    this.authService
      .fxPost<{ uuid: string }>(
        payload,
        'get-user-info',
        `task=${payload.task}`
      )
      .subscribe({
        next: response => {
          this.alerts.addAlert({
            message: `Term Sheet Request Sent. UUID: ${response.uuid}`,
            type: 'success',
            config: {
              closeButton: true,
              timeOut: 5000
            }
          });
        },
        error: () => {
          this.alerts.addAlert({
            message: 'Failed to send request',
            type: 'error',
            config: {
              closeButton: true,
              timeOut: 5000
            }
          });
        }
      });
  }

  private updateLegAttributeColor(
    legComponent: LegComponent,
    lookupKey: string,
    newColor: string
  ): void {
    const legToAlter = legComponent.legComponents.find(
      _ => _.attribute.key === lookupKey
    );
    if (legToAlter) {
      legToAlter.attribute.textColor = newColor;
    }
  }

  private updateLegsToAggregate(legsToAggregate: LegsToAggregate): void {
    this.tabsState.update(state => {
      const clonedState = clone(state);
      clonedState.tabs[clonedState.selectedTab].legsToAggregate =
        legsToAggregate;
      return clonedState;
    });
  }

  private handleRfqButtonState(): void {
    this.rfqButtonSettings.rfqDisabled = this.isRfqDisabled;
    this.rfqToolTipMessage();
  }
}
